import React, {ChangeEvent, useEffect, useState} from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import { get } from "lodash";
import { connect, useSelector } from "react-redux";
import { Field, FieldProps } from "formik";
import { Data } from "../../../../../store/terminal/types";
import { ConfigPropExtenderHoc } from "../../../../../common/StaticConfigProvider";
import OptionalToolTip from "../../../../../common/toolTip/OptionalToolTip";
import { ApplicationState } from "../../../../../store";
import TextInput from "../../../../../common/formElements/TextInput";
import { useFormikContext } from "formik";
import CustomizedSwitch from "../../../../../common/formElements/Switch";
import TimeInput from "../../../../../common/formElements/TimeInput";
import { isNil } from "ramda";
import AutocompleteField from "../../../../../common/formElements/AutocompleteField";

interface FormInput extends Data {
  _prepared?: boolean;
}

interface ConfigurationProps {
  initialValues: FormInput;
  toolTips?: { terminals?: { options?: Record<string, string> } };
  isUpdateAllowed?: boolean;
  shouldHideField: (fieldName: string) => boolean;
}

interface ConfigurationHocProps {
  initialValues: FormInput;
  toolTips?: { terminals?: { options?: Record<string, string> } };
  isUpdateAllowed?: boolean;
  shouldHideField: (fieldName: string) => boolean;
}

export const getValueForField = (
  field: string,
  checked: boolean
): string | boolean => {
  const fields = ["attributes.AutoSoftwareUpdateEnabled"];
  return fields.includes(field) ? (checked ? "Yes" : "No") : checked;
};

const ConfigurationOther = (props: ConfigurationHocProps) => {
  const [disableSoftwareUpdate, setDisableSoftwareUpdate] = useState(false);
  const formikProps = useFormikContext();
  const values = (formikProps.values as unknown) as Data;
  const { setFieldValue, setFieldTouched } = formikProps;

  const hasNo = values.attributes.AutoSoftwareUpdateEnabled == "No";
  const isNab = process.env.IDP === "NAB";

  const { initialValues, isUpdateAllowed, shouldHideField } = props;
  const medPriority = [
    "MobileWifiEthernet",
    "MobileEthernetWifi",
    "WifiMobileEthernet",
    "WifiEthernetMobile",
    "EthernetMobileWifi",
    "EthernetWifiMobile",
    "Any",
  ];
  const termState = ["RKI191", "RKI193", "Login", "Payment", "Suspended"];
  const terminalBackgrounds = useSelector((state: ApplicationState) => Array.isArray(state.terminalBackground.data) ? state.terminalBackground.data : []);
  const tooltipMediumPriority = "Medium Priority";
  const tooltipState = "Terminal State";
  const tooltipTempPassword = "Temp Password";
  const tooltipIdleBitmap = "Background Logo";

  const tooltipFeatureRefundLimit = "FeatureRefundLimit";
  const tooltipFeatureRefundDailyLimit = "FeatureRefundDailyLimit";

  const tooltipBatteryLimit = "BatteryLimit";

  const tooltipAutoSoftwareUpdateEnabled = "AutoSoftwareUpdateEnabled";
  const tooltipPinRetryAttemptCount = "PinRetryAttemptCount";

  useEffect(() => {
    values.attributes.AutoSoftwareUpdateEnabled == "Yes"
      ? setDisableSoftwareUpdate(true)
      : setDisableSoftwareUpdate(false);
  }, []);

  const centToDollar = (cents: number) => {
    return (cents / 100).toFixed(2).toString(); // convert cents to dollars and round to 2 decimal places
  };

  const [dollarValues, setDollarValues] = useState({
    FeatureRefundLimit: !isNaN(values.attributes.FeatureRefundLimit) ? centToDollar(values.attributes.FeatureRefundLimit) : "",
    FeatureRefundDailyLimit: !isNaN(values.attributes.FeatureRefundDailyLimit) ? centToDollar(values.attributes.FeatureRefundDailyLimit) : "",
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    const fieldName = event.target.name;
    const key = fieldName.replace("attributes.", "");

    setFieldTouched(fieldName, true);

    setDollarValues((prevState) => ({
      ...prevState,
      [key]: input,
    }));

    const inputInCents = Math.round(parseFloat(input) * 100);
    const fieldValue = isNaN(inputInCents) ? "" : inputInCents;
    setFieldValue(fieldName, fieldValue);
  };

  return (
    <Grid container spacing={2}>
      {!shouldHideField("attributes.MediumPriority") && (
        <Grid item xs={12} md={4}>
          <Field name="attributes.MediumPriority">
            {({ field }: FieldProps) => (
              <FormControl fullWidth variant="outlined">
                <InputLabel id="med-pri-label">Medium Priority</InputLabel>
                <OptionalToolTip
                  tooltip={get(
                    props,
                    `toolTips.terminals.options.configuration.["${tooltipMediumPriority}"]`
                  )}
                >
                  <Select
                    {...field}
                    fullWidth
                    labelId="med-pri-label"
                    label="Medium Priority"
                    variant="outlined"
                    disabled={!isUpdateAllowed}
                  >
                    {medPriority?.map((val) => {
                      return (
                        <MenuItem key={val} value={val}>
                          {val}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </OptionalToolTip>
              </FormControl>
            )}
          </Field>
        </Grid>
      )}
      
      {!shouldHideField("attributes.State") && (
        <Grid item xs={12} md={4}>
          <Field name="attributes.State">
            {({ field }: FieldProps) => (
              <FormControl fullWidth variant="outlined">
                <InputLabel id="term-state-label">Terminal State</InputLabel>
                <OptionalToolTip
                  tooltip={get(
                    props,
                    `toolTips.terminals.options.configuration.["${tooltipState}"]`
                  )}
                >
                  <Select
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Terminal State"
                    disabled={!isUpdateAllowed || isNab}
                  >
                    {termState?.map((val) => {
                      return (
                        <MenuItem
                          key={val}
                          value={val}
                          disabled={
                            val !== "Suspended" &&
                            val !== initialValues.attributes?.State
                          }
                        >
                          {val}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </OptionalToolTip>
              </FormControl>
            )}
          </Field>
        </Grid>
      )}
      
      {!shouldHideField("attributes.TempPassword") && (
        <Grid item xs={12} md={4}>
          <Field name="attributes.TempPassword">
            {({ field }: FieldProps) => (
              <OptionalToolTip
                tooltip={get(
                  props,
                  `toolTips.terminals.options.configuration.["${tooltipTempPassword}"]`
                )}
              >
                <TextInput
                  fieldname={field.name}
                  label="Temp password"
                  disabled={!isUpdateAllowed}
                />
              </OptionalToolTip>
            )}
          </Field>
        </Grid>
      )}
      
      {!shouldHideField("attributes.IdleBitmap") && (
        <Grid item xs={12} md={4}>
          <OptionalToolTip
            tooltip={get(
              props,
              `toolTips.terminals.options.configuration.["${tooltipIdleBitmap}"]`
            )}
          >
            <AutocompleteField
              key={"terminal-logos-" + terminalBackgrounds.length}
              fieldname="attributes.IdleBitmap"
              options={terminalBackgrounds}
              value={terminalBackgrounds.find((c) => c.attributes.FileName === values.attributes.IdleBitmap)}
              onChange={(e, value) =>
                setFieldValue("attributes.IdleBitmap", !isNil(value) ? value.attributes.FileName : "")
              }
              getOptionLabel={(option): string =>
                `${option.attributes.FileName}`
              }
              renderOption={(props, option) => (
                <div
                  {...props}
                >{`${option.attributes.FileName}`}</div>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Background Logo" variant="outlined" />
              )}
              disabled={!isUpdateAllowed}
            />
          </OptionalToolTip>
        </Grid>
      )}
      
      {!shouldHideField("attributes.FeatureRefundLimit") && (
        <Grid item xs={12} md={4}>
          <OptionalToolTip
            tooltip={get(
              props,
              `toolTips.terminals.options.configuration.["${tooltipFeatureRefundLimit}"]`
            )}
          >
            <TextField
              type="number"
              value={dollarValues["FeatureRefundLimit"]}
              onChange={handleChange}
              name="attributes.FeatureRefundLimit"
              variant="outlined"
              label="Refund Transaction Limit"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">$</InputAdornment>
                ),
              }}
              disabled={!isUpdateAllowed}
              fullWidth
            />
          </OptionalToolTip>
        </Grid>
      )}
      
      {!shouldHideField("attributes.FeatureRefundDailyLimit") && (
        <Grid item xs={12} md={4}>
          <OptionalToolTip
            tooltip={get(
              props,
              `toolTips.terminals.options.configuration.["${tooltipFeatureRefundDailyLimit}"]`
            )}
          >
            <TextField
              type="number"
              value={dollarValues["FeatureRefundDailyLimit"]}
              onChange={handleChange}
              name="attributes.FeatureRefundDailyLimit"
              label="Refund Daily Limit"
              disabled={!isUpdateAllowed}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">$</InputAdornment>
                ),
              }}
            />
          </OptionalToolTip>
        </Grid>
      )}
      
      {!shouldHideField("attributes.BatteryLimit") && (
        <Grid item xs={12} md={4}>
          <Field name="attributes.BatteryLimit">
            {({ field }: FieldProps) => (
              <OptionalToolTip
                tooltip={get(
                  props,
                  `toolTips.terminals.options.configuration.["${tooltipBatteryLimit}"]`
                )}
              >
                <TextInput
                  fieldname={field.name}
                  label="Battery Limit"
                  disabled={!isUpdateAllowed}
                />
              </OptionalToolTip>
            )}
          </Field>
        </Grid>
      )}
      
      {!shouldHideField("attributes.AutoSoftwareUpdateEnabled") && (
        <Grid item xs={12} md={4} alignItems="center" display="flex">
          <OptionalToolTip
            tooltip={get(
              props,
              `toolTips.terminals.options.configuration.["${tooltipAutoSoftwareUpdateEnabled}"]`
            )}
          >
            <CustomizedSwitch
              checked={!hasNo}
              label="Auto Software/Firmware Update Enabled"
              onChange={(event, value) => {
                if (value) {
                  setDisableSoftwareUpdate(true);
                  setFieldValue(
                    "attributes.AutoSoftwareUpdateEnabled",
                    getValueForField(
                      "attributes.AutoSoftwareUpdateEnabled",
                      !disableSoftwareUpdate
                    )
                  );
                } else {
                  setDisableSoftwareUpdate(false);
                  setFieldValue(
                    "attributes.AutoSoftwareUpdateEnabled",
                    getValueForField(
                      "attributes.AutoSoftwareUpdateEnabled",
                      !disableSoftwareUpdate
                    )
                  );
                }
              }}
            />
          </OptionalToolTip>
        </Grid>
      )}
      
      {!shouldHideField("attributes.AutoUpdateTimeFrom") && (
        <Grid item xs={12} alignItems="center" md={2}>
          <TimeInput
            fieldname="attributes.AutoUpdateTimeFrom"
            label="Time From"
            onChange={(val) => {
              setFieldValue("attributes.AutoUpdateTimeFrom", val)
            }}
            value={values.attributes.AutoUpdateTimeFrom as string}
            disabled={!disableSoftwareUpdate}
          />
        </Grid>
      )}
      
      {!shouldHideField("attributes.AutoUpdateTimeTo") && (
        <Grid item xs={12} alignItems="center" md={2}>
          <TimeInput
            fieldname="attributes.AutoUpdateTimeTo"
            label="Time To"
            onChange={(val) => {
              setFieldValue("attributes.AutoUpdateTimeTo", val)
            }}
            value={values.attributes.AutoUpdateTimeTo as string}
            disabled={!disableSoftwareUpdate}
          />
        </Grid>
      )}
      
      {!shouldHideField("attributes.PinRetryAttemptCount") && (
        <Grid item xs={12} md={4}>
          <Field name="attributes.PinRetryAttemptCount">
            {({ field }: FieldProps) => (
              <OptionalToolTip
                tooltip={get(
                  props,
                  `toolTips.terminals.options.configuration.["${tooltipPinRetryAttemptCount}"]`
                )}
              >
                <TextInput
                  type="number"
                  fieldname={field.name}
                  label="Pin Retry Attempt Count"
                  disabled={!isUpdateAllowed}
                />
              </OptionalToolTip>
            )}
          </Field>
        </Grid>
      )}
    </Grid>
  );
};

const ConnectedConfigurationOther = connect(
  null,
  null,
  (_stateProps, _dispatchProps, ownProps: ConfigurationHocProps) => {
    return {
      ...ownProps,
      shouldHideField: ownProps.shouldHideField,
    };
  }
)(ConfigurationOther);

const ConnectedConfigurationOtherHoc = (props: ConfigurationProps) => (
  <ConfigPropExtenderHoc propName="toolTips">
    <ConnectedConfigurationOther {...props} />
  </ConfigPropExtenderHoc>
);

export default ConnectedConfigurationOtherHoc;
